import { and, collection, getDocs, query, Timestamp, where } from "firebase/firestore";
import { GetListParams, GetListResult, GetManyReferenceParams, GetManyReferenceResult } from "react-admin";
import { DrivingLesson } from "../model/autovioCalendarEvents";
import { firestore } from "../firebase";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { drivingLessonConverter } from "./converter/drivingLessonConverter";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { QueryFilterConstraint } from "@firebase/firestore";
import { isDateRange, isValidInterval } from "../model/DateRange";

/**
 * Does only provide normal and special driving lessons (no simulator lessons, no exams).
 */
class DrivingLessonsProvider extends AbstractDataProvider<DrivingLesson> {
  async getList(_resource: string, { filter, sort, pagination }: GetListParams): Promise<GetListResult<DrivingLesson>> {
    const { drivingSchoolId, instructorId, studentId, drivingLessonType, dateRange, ...otherFilters } = filter;
    if (!drivingSchoolId) {
      throw new Error("filter.drivingSchoolId is required");
    }
    const queryFilterConstraints: Array<QueryFilterConstraint> = [
      where("drivingSchoolUid", "==", drivingSchoolId),
      where("type", "==", "DrivingLesson"),
      where("deleted", "==", false),
    ];
    if (instructorId) {
      if (typeof instructorId !== "string") {
        throw new Error(`Invalid filter.instructorId: ${JSON.stringify(instructorId)} -- must be a string`);
      }
      queryFilterConstraints.push(where("derived.instructorUids", "array-contains", instructorId));
    }
    if (studentId) {
      if (typeof studentId !== "string") {
        throw new Error(`Invalid filter.studentId: ${JSON.stringify(studentId)} -- must be a string`);
      }
      queryFilterConstraints.push(where("derived.studentUids", "array-contains", studentId));
    }
    if (drivingLessonType) {
      if (typeof drivingLessonType === "string") {
        if (!["normal", "autobahnfahrt", "nachtfahrt", "ueberlandfahrt"].includes(drivingLessonType)) {
          throw new Error(
            `Invalid filter.drivingLessonType: ${JSON.stringify(
              drivingLessonType,
            )} -- must be one of: "normal", "autobahnfahrt", "nachtfahrt", "ueberlandfahrt"`,
          );
        }
        queryFilterConstraints.push(where("drivingLessonType", "==", drivingLessonType));
      } else if (Array.isArray(drivingLessonType)) {
        if (
          !drivingLessonType.every((it) => ["normal", "autobahnfahrt", "nachtfahrt", "ueberlandfahrt"].includes(it))
        ) {
          throw new Error(
            `Invalid filter.drivingLessonType: ${JSON.stringify(
              drivingLessonType,
            )} -- must only contain: "normal", "autobahnfahrt", "nachtfahrt", "ueberlandfahrt"`,
          );
        }
        queryFilterConstraints.push(where("drivingLessonType", "in", drivingLessonType));
      } else {
        throw new Error(
          `Invalid filter.drivingLessonType: ${JSON.stringify(
            drivingLessonType,
          )} -- must be a string or an array of strings`,
        );
      }
    } else {
      queryFilterConstraints.push(
        where("drivingLessonType", "in", ["normal", "ueberlandfahrt", "autobahnfahrt", "nachtfahrt"]),
      );
    }
    if (!dateRange) {
      if (!studentId) {
        throw new Error("Either filter.studentId or filter.dateRange is required");
      }
    } else if (isDateRange(dateRange)) {
      queryFilterConstraints.push(
        where("start", ">=", Timestamp.fromDate(dateRange[0].toDate())),
        where("start", "<", Timestamp.fromDate(dateRange[1].nextDay.toDate())),
      );
    } else if (isValidInterval(dateRange)) {
      queryFilterConstraints.push(
        where("start", ">=", Timestamp.fromDate(dateRange.start.toJSDate())),
        where("start", "<", Timestamp.fromDate(dateRange.end.toJSDate())),
      );
    } else {
      throw new Error(
        `Invalid filter.dateRange: ${JSON.stringify(dateRange)} -- must be a valid DateRange or a valid Interval`,
      );
    }
    const snapshot = await getDocs(query(collection(firestore, "calendar_events"), and(...queryFilterConstraints)));
    const drivingLessonsFromServer = snapshot.docs.map(drivingLessonConverter.fromFirestore);
    const drivingLessons = applyFilter(drivingLessonsFromServer, otherFilters);
    console.info(`Retrieved ${drivingLessons.length} driving lesson(s)`, filter);
    if (drivingLessons.length < drivingLessonsFromServer.length) {
      console.info(`Over-Fetched ${drivingLessonsFromServer.length - drivingLessons.length} driving lesson(s)`);
    }
    return applyPagination(applySort(drivingLessons, sort), pagination);
  }

  async getManyReference(
    resource: string,
    { target, id, filter, sort, pagination, meta }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<DrivingLesson>> {
    if (target !== "drivingSchoolId") {
      throw new Error(`Invalid target: ${JSON.stringify(target)} -- expected: "drivingSchoolId"`);
    }
    return this.getList(resource, { pagination, sort, filter: { ...filter, [target]: id }, meta });
  }
}

export const drivingLessonsProvider = new DrivingLessonsProvider();
